import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useRegulatoryActions, useRegulatoryActionsStats } from '@/hooks/queries'
import { useFilterDefaultsFromPrefs } from '@/hooks/useFilterDefaultsFromPrefs'
import type { RegulatoryAction, RegulatoryActionFilters, Severity } from '@/lib/types'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFineAmount(amount: number): string {
  if (amount >= 1_000_000_000) return `€${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000) return `€${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `€${(amount / 1_000).toFixed(0)}K`
  return `€${amount}`
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US')
}

function formatDate(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : 'Unknown'
}

const SEVERITY_COLORS: Record<Severity, { bg: string; text: string }> = {
  critical: { bg: 'bg-red-900/50', text: 'text-red-300' },
  high: { bg: 'bg-orange-900/50', text: 'text-orange-300' },
  medium: { bg: 'bg-yellow-900/50', text: 'text-yellow-300' },
  low: { bg: 'bg-blue-900/50', text: 'text-blue-300' },
}

// ─── KPI Rail ────────────────────────────────────────────────────────────────

function KpiRail({
  total,
  totalFines,
  byJurisdiction,
  bySeverity,
  isLoading,
}: {
  total: number
  totalFines: number
  byJurisdiction: { jurisdiction: string | null; count: number }[]
  bySeverity: { severity: string | null; count: number }[]
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div data-testid="kpi-rail" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    )
  }

  const topJurisdictions = byJurisdiction.slice(0, 3)
  const severityCounts = bySeverity.filter((s) => s.severity)

  return (
    <div data-testid="kpi-rail" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-muted/50 border rounded-lg p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Actions</div>
        <div className="text-3xl font-bold mt-1">{formatNumber(total)}</div>
      </div>
      <div className="bg-muted/50 border rounded-lg p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Fines (EUR)</div>
        <div className="text-3xl font-bold mt-1">{formatFineAmount(totalFines)}</div>
      </div>
      <div className="bg-muted/50 border rounded-lg p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Top Jurisdictions</div>
        {topJurisdictions.length === 0 ? (
          <div className="text-sm text-muted-foreground mt-2">—</div>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {topJurisdictions.map((j) => (
              <li
                key={j.jurisdiction ?? 'unknown'}
                className="flex justify-between text-sm"
              >
                <span className="truncate">{j.jurisdiction ?? 'Unknown'}</span>
                <span className="text-muted-foreground">{formatNumber(j.count)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="bg-muted/50 border rounded-lg p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">By Severity</div>
        {severityCounts.length === 0 ? (
          <div className="text-sm text-muted-foreground mt-2">—</div>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {severityCounts.map((s) => {
              const sev = s.severity as Severity
              const colors = SEVERITY_COLORS[sev]
              return (
                <li key={sev} className="flex justify-between text-sm">
                  <span
                    className={`${colors?.bg ?? ''} ${colors?.text ?? ''} text-xs font-semibold px-2 py-0.5 rounded`}
                  >
                    {sev.toUpperCase()}
                  </span>
                  <span className="text-muted-foreground">{formatNumber(s.count)}</span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

// ─── Breach Disclosures ──────────────────────────────────────────────────────

function BreachDisclosures({
  disclosures,
  isLoading,
}: {
  disclosures: RegulatoryAction[]
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div>
        <h2 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          SEC Breach Disclosures (Item 1.05)
        </h2>
        <Skeleton className="h-24 rounded-lg" />
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-red-500" />
        SEC Breach Disclosures (Item 1.05)
      </h2>
      {disclosures.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-6 text-center">
          No breach disclosures yet
        </div>
      ) : (
        <div className="space-y-3">
          {disclosures.map((d) => {
            const details = d.details as Record<string, unknown> | null
            const ticker = details?.ticker as string | null
            const formType = details?.formType as string | null
            const cik = details?.cik as string | null
            return (
              <div
                key={d.id}
                className="bg-muted/50 border border-red-900/50 border-l-[3px] border-l-red-500 rounded-lg p-4"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold">
                      {d.organisationName}
                      {ticker && <span className="text-muted-foreground"> ({ticker})</span>}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Filed {d.decisionDate?.slice(0, 10) ?? 'Unknown'}
                      {formType && ` · ${formType}`}
                      {' · Item 1.05'}
                      {cik && ` · CIK ${cik}`}
                    </div>
                    {d.summary && (
                      <p className="text-sm mt-2 line-clamp-2">{d.summary}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="bg-red-900/50 text-red-300 text-xs font-semibold px-2 py-0.5 rounded">
                      HIGH
                    </span>
                    {d.sourceUrl && (
                      <a
                        href={d.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        SEC Filing <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-2 text-center">
        Showing all breach disclosures · Updated hourly from SEC EDGAR
      </p>
    </div>
  )
}

// ─── Enforcement Table ───────────────────────────────────────────────────────

function EnforcementTable({
  actions,
  total,
  page,
  limit,
  onPageChange,
  isLoading,
}: {
  actions: RegulatoryAction[]
  total: number
  page: number
  limit: number
  onPageChange: (p: number) => void
  isLoading: boolean
}) {
  const totalPages = Math.ceil(total / limit)
  const from = (page - 1) * limit + 1
  const to = Math.min(page * limit, total)

  return (
    <div>
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded" />
          ))}
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left p-3 font-medium text-muted-foreground">Organisation</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Authority</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Country</th>
                  <th className="text-right p-3 font-medium text-muted-foreground">Fine (EUR)</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Severity</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Decision date</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Record updated</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">GDPR Articles</th>
                </tr>
              </thead>
              <tbody>
                {actions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      No enforcement actions match your filters
                    </td>
                  </tr>
                ) : (
                  actions.map((a) => {
                    const details = a.details as Record<string, unknown> | null
                    const authority = (details?.authority as string) ?? ''
                    const fineAmount = (details?.fineAmount as number) ?? 0
                    const gdprArticles = (details?.gdprArticles as string[]) ?? []
                    const sev = a.severity as Severity | null
                    const colors = sev ? SEVERITY_COLORS[sev] : null
                    return (
                      <tr key={a.id} className="border-b last:border-b-0 hover:bg-muted/30">
                        <td className="p-3 font-medium">{a.organisationName}</td>
                        <td className="p-3 text-muted-foreground">{authority}</td>
                        <td className="p-3 text-muted-foreground">{a.jurisdiction ?? ''}</td>
                        <td className="p-3 text-right font-semibold">{formatNumber(fineAmount)}</td>
                        <td className="p-3">
                          {colors && (
                            <span className={`${colors.bg} ${colors.text} text-xs font-semibold px-2 py-0.5 rounded`}>
                              {sev!.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {a.decisionDate ? (
                            formatDate(a.decisionDate)
                          ) : (
                            <span
                              data-testid="decision-date-unknown"
                              className="bg-muted text-muted-foreground text-xs font-medium px-2 py-0.5 rounded border border-dashed"
                              title="Decision date not provided by upstream tracker"
                            >
                              Date unknown
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">
                          {formatDate(a.updatedAt)}
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">{gdprArticles.join(', ')}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <div className="flex justify-between items-center mt-3 text-sm">
              <span className="text-muted-foreground text-xs">
                Showing {from}–{to} of {formatNumber(total)}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                  className="px-3 py-1 text-xs rounded border disabled:opacity-30 hover:bg-muted/50"
                >
                  ← Prev
                </button>
                <span className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded">
                  {page}
                </span>
                <span className="px-3 py-1 text-xs text-muted-foreground">
                  / {totalPages}
                </span>
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="px-3 py-1 text-xs rounded border disabled:opacity-30 hover:bg-muted/50"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function RegulatoryActionsPage() {
  // P5 adoption (Wave-1 spec rule §10): seed page-level filter state from
  // the user's saved-search row keyed `regulatory-actions`. The hook returns
  // `undefined` when there is no saved row — existing users keep the global
  // view unchanged (Captain Input #4).
  const { defaults: savedDefaults } =
    useFilterDefaultsFromPrefs<RegulatoryActionFilters>('regulatory-actions')

  const [page, setPage] = useState(1)
  const [jurisdiction, setJurisdiction] = useState<string>(
    typeof savedDefaults?.jurisdiction === 'string' ? savedDefaults.jurisdiction : 'all',
  )
  const [severity, setSeverity] = useState<string>(
    typeof savedDefaults?.severity === 'string' ? savedDefaults.severity : 'all',
  )
  const [search, setSearch] = useState<string>(
    typeof savedDefaults?.search === 'string' ? savedDefaults.search : '',
  )

  // Stage 2: read optional `framework=<slug>` from URL so P3 card click lands
  // with a pre-applied filter. The API accepts any slug; unknown/pending-ingest
  // slugs return zero rows by design.
  const [searchParams] = useSearchParams()
  const frameworkFromUrl = searchParams.get('framework') ?? undefined

  const { data: stats, isLoading: statsLoading } = useRegulatoryActionsStats()

  const { data: breachData, isLoading: breachLoading } = useRegulatoryActions({
    type: 'breach_disclosure',
    framework: frameworkFromUrl,
    limit: 50,
  })

  // A1g forward fix: search is now server-side. Empty string → undefined so
  // the Zod min(1) bound doesn't reject the request.
  const trimmedSearch = search.trim()
  const { data: enforcementData, isLoading: enforcementLoading } = useRegulatoryActions({
    type: 'enforcement_action',
    jurisdiction: jurisdiction !== 'all' ? jurisdiction : undefined,
    severity: severity !== 'all' ? severity : undefined,
    framework: frameworkFromUrl,
    search: trimmedSearch.length > 0 ? trimmedSearch : undefined,
    page,
    limit: 20,
  })

  return (
    <main className="flex flex-col gap-6 p-6 max-w-7xl mx-auto">
      <header>
        <h1 className="text-2xl font-semibold">Regulatory Actions</h1>
        <p className="text-muted-foreground">SEC breach disclosures and GDPR enforcement actions</p>
      </header>

      <KpiRail
        total={stats?.total ?? 0}
        totalFines={stats?.totalFines ?? 0}
        byJurisdiction={stats?.byJurisdiction ?? []}
        bySeverity={stats?.bySeverity ?? []}
        isLoading={statsLoading}
      />

      <section>
        <BreachDisclosures
          disclosures={breachData?.data ?? []}
          isLoading={breachLoading}
        />
      </section>

      <section>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
          <h2 className="text-sm font-semibold">GDPR Enforcement Actions</h2>
          <div className="flex gap-2 flex-wrap">
            <Input
              placeholder="Search organisation…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-40 h-8 text-xs"
            />
            <Select value={jurisdiction} onValueChange={(v) => { setJurisdiction(v); setPage(1) }}>
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue placeholder="All jurisdictions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All jurisdictions</SelectItem>
                {stats?.byJurisdiction?.map((j) => (
                  <SelectItem key={j.jurisdiction ?? 'unknown'} value={j.jurisdiction ?? 'unknown'}>
                    {j.jurisdiction ?? 'Unknown'} ({j.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={severity} onValueChange={(v) => { setSeverity(v); setPage(1) }}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue placeholder="All severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <EnforcementTable
          actions={enforcementData?.data ?? []}
          total={enforcementData?.pagination?.total ?? 0}
          page={page}
          limit={20}
          onPageChange={setPage}
          isLoading={enforcementLoading}
        />
      </section>
    </main>
  )
}
